import {Storage} from "@/services/Storage";
import Swal from "sweetalert2";

export default {
    name: 'backOffice',
    state: {},
    getters: {},
    mutations: {},
    actions: {
        /**
         * Open the Online BackOffice (web) or Lytning Core (Electron) in
         * a way that hydrates the target user without ever putting the
         * password on the wire / in the deeplink URL.
         *
         * <p>Flow:
         * <ol>
         *   <li>The sessionId was minted at LOGIN time by
         *       {@code POST /posAuth/{storeDB}/login} on the Franchise
         *       Online Server V2 (port 7082). The opaque token is
         *       persisted in IndexedDB ({@code CURRENT_SESSION}) by
         *       {@code posLogin} in user_module.js. No password is
         *       ever stored.</li>
         *   <li>This action reads the persisted session from
         *       {@code Storage.getCurrentSession(storeDB)} and opens the
         *       target URL with {@code ?session=<id>&store=<DB>&page=<name>}.</li>
         *   <li>The target app calls
         *       {@code GET /posAuth/{storeDB}/session/{id}} to hydrate
         *       {@code setCurrentUser} and strip the URL params.</li>
         * </ol>
         *
         * <p>Why the password is no longer in the URL: the previous
         * implementation used {@code ?auth=btoa(JSON.stringify({...user.Password}))}.
         * {@code btoa} is base64, not encryption. Any other app
         * registered for the same custom scheme, any accessibility tool,
         * and any screenshot of the protocol handler can read it. The
         * opaque {@code sessionId} is a single-use, short-lived token
         * that resolves server-side to a user row.
         *
         * <p>Failure modes:
         * <ul>
         *   <li><b>No persisted session</b> (user never logged in via
         *       posLogin, or the IndexedDB was wiped): open the bare
         *       BackOffice URL with a "please sign in" toast. Do NOT
         *       fall back to {@code ?auth=}.</li>
         *   <li><b>Expired session</b> ({@code sessionExpiresAt} is in
         *       the past): same as above; force a re-login on the bare
         *       URL.</li>
         *   <li><b>Wrong store</b> (session was minted for a different
         *       storeDB than the one currently active): same.</li>
         * </ul>
         *
         * @param context the Vuex action context
         * @param page    the requested destination page
         *                ({@code {name: 'stock' | 'payouts' | 'cashdrop'}})
         */
        openOnlineBackOffice: async (context, page = {name: `stock`}) => {
            const store = await Storage.getDefaultStore();
            if (!store || !store.StoreDB) {
                Swal.fire({
                    icon: 'error',
                    title: 'Cannot open BackOffice',
                    text: 'No active store. Please select a store first.'
                });
                window.open(backOfficePlatform, '_blank');
                return;
            }

            // 1. Read the persisted sessionId minted at login.
            //    getCurrentSession returns null when the session is
            //    missing, expired, or for a different store.
            const session = await Storage.getCurrentSession(store.StoreDB);
            if (!session || !session.sessionId) {
                // eslint-disable-next-line no-console
                console.warn('[openOnlineBackOffice] no valid deeplink session; opening bare URL');
                Swal.fire({
                    icon: 'warning',
                    title: 'Please sign in again',
                    text: 'Your BackOffice sign-in has expired. Open the link and sign in manually.'
                });
                window.open(backOfficePlatform, '_blank');
                return;
            }

            // 2. Build the deeplink URL. storeDB is in the URL so the
            //    receiving app can hydrate even on cold cache (no
            //    localStorage / IndexedDB available on first paint).
            const url = `${backOfficePlatform}?session=${encodeURIComponent(session.sessionId)}`
                      + `&store=${encodeURIComponent(store.StoreDB)}`
                      + `&page=${encodeURIComponent(page.name || 'stock')}`;
            return openUrl(url);
        },
        searchExpenseAccounts: async (context, accountName) => {
            let store = await Storage.getDefaultStore();
            return axios.get(backOfficeTomcatApi + `chartOfAccounts/${store.StoreDB}/searchExpenseAccounts?accountName=` + accountName)
                .then(({data}) => data).catch(err => errorHandler.tomcatError(err));
        },
        capturePayout: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            let pcName = await Device.getPCName();
            
            payload.User = user.Username;
            payload.PC = pcName;
            return axios.post(backOfficeTomcatApi + `Payments/${store.StoreDB}/storePayout`, payload)
                .then(({data}) => data).catch(err => errorHandler.tomcatError(err));
        },
        captureCashDrop: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            let settings = await Storage.getDeviceSettings();
            payload.user = user.Username;
            payload.pc = settings.PC;
            return axios.post(backOfficeTomcatApi + `safeFunctions/${store.StoreDB}/cashierDepositToSafe`, payload)
                .then(({data}) => data).catch(err => errorHandler.tomcatError(err));
        },
        getCashSafes: async () => {
            let store = await Storage.getDefaultStore();
            return axios.get(backOfficeTomcatApi + `chartOfAccounts/${store.StoreDB}/getSafeByType?type=CASH`)
                .then(({data}) => data).catch(err => errorHandler.tomcatError(err))
        }
    }
}
